
import { QueueCard } from "@/components/QueueCard";
import { Queue } from "@/types/queue";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface QueueListProps {
  queues: Queue[];
  onEdit: (queue: Queue) => void;
  onClick: (queue: Queue) => void;
  onDelete: (queue: Queue) => void;
  onCreateQueue: (queue: Omit<Queue, "id">) => Promise<void>;
}

export const QueueList = ({ queues, onEdit, onClick, onDelete }: QueueListProps) => {
  const [queueToDelete, setQueueToDelete] = useState<Queue | null>(null);
  const { toast } = useToast();

  const handleDelete = (queue: Queue) => {
    if (!queue.can_be_deleted) {
      toast({
        title: "Cannot Delete Queue",
        description: "This queue cannot be deleted as it was created during your trial period which has expired. Please upgrade your plan to manage this queue.",
        variant: "destructive",
      });
      return;
    }
    setQueueToDelete(queue);
  };

  const confirmDelete = () => {
    if (queueToDelete) {
      onDelete(queueToDelete);
      setQueueToDelete(null);
    }
  };

  if (queues.length === 0) {
    return (
      <div className="relative overflow-hidden text-center py-12 bg-gradient-to-r from-card to-secondary/30 rounded-xl shadow-sm border border-muted/20">
        <div className="absolute inset-0 bg-grid-primary/5 [mask-image:linear-gradient(0deg,rgba(17,17,17,1),rgba(17,17,17,0.6))]" />
        <div className="relative">
          <h3 className="text-lg font-semibold mb-2 text-foreground">No queues yet</h3>
          <p className="text-muted-foreground mb-6">Create your first queue to get started</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {queues.map((queue) => (
          <QueueCard
            key={queue.id}
            queue={queue}
            onEdit={onEdit}
            onClick={onClick}
            onDelete={handleDelete}
          />
        ))}
      </div>

      <AlertDialog open={!!queueToDelete} onOpenChange={(open) => !open && setQueueToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the queue "{queueToDelete?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
